/**
 * CompetitionAnalytics.jsx (iter230) — Superadmin-only funnel of demo
 * judge activity. Buckets `demo_events` by scenario, event, and hashed
 * IP so the owner can see who reached each stage.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Trophy, ArrowClockwise, Users, ChartBar, Path,
  CheckCircle, ArrowSquareOut, Key, Copy, Warning,
  Ticket, Plus, Prohibit,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  getCompetitionAnalytics,
  rotateCompetitionDemoPassword,
  listJudgeCodes,
  createJudgeCode,
  revokeJudgeCode,
} from '../lib/api';

export default function CompetitionAnalytics() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  // Password rotation state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [customPw, setCustomPw] = useState('');
  const [rotating, setRotating] = useState(false);
  const [rotatedResult, setRotatedResult] = useState(null); // { email, password, rotated_at }

  const doRotate = async (useCustom) => {
    if (useCustom && customPw.trim().length < 8) {
      toast.error('Custom password must be at least 8 characters');
      return;
    }
    if (!window.confirm(
      useCustom
        ? 'Set a new custom password? Every existing judge session will keep working until they log out; new judges will need the new password.'
        : 'Generate a new random password? The old one will stop working for new logins immediately.'
    )) return;
    setRotating(true);
    try {
      const r = await rotateCompetitionDemoPassword(useCustom ? customPw.trim() : null);
      setRotatedResult(r.data);
      setCustomPw('');
      toast.success('Judge password rotated. Copy it now — it will not be shown again.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotation failed');
    } finally { setRotating(false); }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(rotatedResult?.password || '');
      toast.success('Password copied to clipboard');
    } catch (_) {
      toast.error('Clipboard blocked — copy the value manually');
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const r = await getCompetitionAnalytics();
      setData(r.data);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(e?.response?.data?.detail || 'Analytics fetch failed');
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (denied) {
    return (
      <Layout>
        <div className="p-8 max-w-lg" data-testid="ca-denied">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-red-600 mb-2">Access denied</div>
          <h1 className="text-2xl font-black mb-2">Superadmin only</h1>
          <p className="text-sm text-muted-foreground">
            Judge analytics are restricted to the Stockaris platform superadmin.
            Ask the owner (or nobbyjr@hotmail.com) to send you a link.
          </p>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="p-6 text-sm text-muted-foreground" data-testid="ca-loading">
          {busy ? 'Loading demo analytics…' : 'Preparing…'}
        </div>
      </Layout>
    );
  }

  const f = data.funnel || {};
  const maxEvent = Math.max(1, ...(data.counts_by_event || []).map((r) => r.count));

  return (
    <Layout>
      <div className="space-y-6 p-1" data-testid="competition-analytics-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-600 mb-1">
              <Trophy size={14} weight="duotone" /> Superadmin · Competition Demo
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">Judge Analytics</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Anonymous funnel from the Competition Demo. Judges are bucketed by hashed IP fingerprint.
            </p>
            {data.window?.from && (
              <p className="text-xs text-muted-foreground mt-1">
                Window: {new Date(data.window.from).toLocaleString()} → {new Date(data.window.to).toLocaleString()}
              </p>
            )}
          </div>
          <Button onClick={load} disabled={busy} variant="outline"
            className="rounded-none uppercase tracking-widest text-xs" data-testid="ca-refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> {busy ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {/* Funnel */}
        <Card className="p-4 rounded-none">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">
            Judge funnel · {data.unique_judges} unique judges · {data.total_events.toLocaleString()} events
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <FunnelStep icon={Users} label="Started"      value={f.started}              tone="sky" testId="ca-funnel-started" />
            <FunnelStep icon={Path}  label="Scenarios opened" value={f.scenarios_opened} tone="amber" testId="ca-funnel-scenarios" />
            <FunnelStep icon={CheckCircle} label="Tours finished"  value={f.tours_finished}   tone="emerald" testId="ca-funnel-tours" />
            <FunnelStep icon={ArrowClockwise} label="Resets"         value={f.resets}            tone="red"     testId="ca-funnel-resets" />
            <FunnelStep icon={Trophy} label="Challenge complete" value={f.completed_challenge} tone="emerald" testId="ca-funnel-completed" />
          </div>
        </Card>

        {/* Judge password rotation */}
        <Card className="p-4 rounded-none border-l-4 border-l-emerald-500" data-testid="ca-rotate-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-600 mb-1">
                <Key size={12} weight="duotone" /> Judge Password
              </div>
              <div className="text-sm font-bold">Rotate the Competition Demo password</div>
              <p className="text-xs text-muted-foreground mt-1">
                Generate a new password (or set a custom one) and hand it out to your next batch of judges.
                The old password stops working immediately. Existing logged-in sessions stay active until logout.
              </p>
            </div>
            {!rotateOpen && (
              <Button
                onClick={() => setRotateOpen(true)}
                variant="outline"
                className="rounded-none uppercase tracking-widest text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="ca-rotate-open"
              >
                <Key size={14} className="mr-1" /> Rotate Password
              </Button>
            )}
          </div>

          {rotateOpen && !rotatedResult && (
            <div className="mt-4 space-y-3 border-t pt-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => doRotate(false)}
                  disabled={rotating}
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-widest"
                  data-testid="ca-rotate-random"
                >
                  {rotating ? 'Generating…' : 'Generate random 20-char password'}
                </Button>
                <span className="text-xs text-muted-foreground">or</span>
                <input
                  type="text"
                  value={customPw}
                  onChange={(e) => setCustomPw(e.target.value)}
                  placeholder="Custom password (min 8 chars)"
                  className="border rounded-none px-2 py-1 text-sm font-mono min-w-[260px]"
                  data-testid="ca-rotate-custom-input"
                  autoComplete="off"
                />
                <Button
                  onClick={() => doRotate(true)}
                  disabled={rotating || customPw.trim().length < 8}
                  variant="outline"
                  className="rounded-none text-xs uppercase tracking-widest"
                  data-testid="ca-rotate-custom-submit"
                >
                  Set custom
                </Button>
                <Button
                  onClick={() => { setRotateOpen(false); setCustomPw(''); }}
                  variant="ghost"
                  className="rounded-none text-xs uppercase tracking-widest text-muted-foreground"
                  data-testid="ca-rotate-cancel"
                >
                  Cancel
                </Button>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                <span>
                  This bypasses the <code>COMPETITION_DEMO_PASSWORD</code> env variable. If the pod is
                  redeployed the env value will re-seed the password on next demo reset unless you also
                  update the deployment secret.
                </span>
              </div>
            </div>
          )}

          {rotatedResult && (
            <div className="mt-4 border-t pt-3 space-y-3" data-testid="ca-rotate-result">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-600">
                New password · will not be shown again
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="bg-neutral-900 text-emerald-300 px-3 py-2 font-mono text-sm select-all break-all"
                  data-testid="ca-rotate-password-value"
                >
                  {rotatedResult.password}
                </code>
                <Button
                  onClick={copyPw}
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-widest"
                  data-testid="ca-rotate-copy"
                >
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
                <Button
                  onClick={() => { setRotatedResult(null); setRotateOpen(false); }}
                  variant="outline"
                  className="rounded-none text-xs uppercase tracking-widest"
                  data-testid="ca-rotate-done"
                >
                  Done
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Email: <span className="font-mono">{rotatedResult.email}</span> · Rotated at{' '}
                {new Date(rotatedResult.rotated_at).toLocaleString()} · Users updated:{' '}
                {rotatedResult.users_updated}
              </div>
            </div>
          )}
        </Card>

        <JudgeCodesCard />

        {/* Events + scenarios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Card className="p-4 rounded-none">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">
              <ChartBar size={12} /> Events (by count)
            </div>
            <ul className="space-y-1" data-testid="ca-events-list">
              {data.counts_by_event.map((r) => (
                <li key={r.event} className="flex items-center gap-2 text-[11px]">
                  <div className="w-56 shrink-0 font-mono">{r.event}</div>
                  <div className="flex-1 h-3 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${(r.count / maxEvent) * 100}%` }} />
                  </div>
                  <div className="w-12 text-right tabular-nums">{r.count}</div>
                </li>
              ))}
              {data.counts_by_event.length === 0 && (
                <li className="text-xs italic text-muted-foreground py-3">No events yet.</li>
              )}
            </ul>
          </Card>
          <Card className="p-4 rounded-none">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">
              <Path size={12} /> Scenarios (opened / interacted)
            </div>
            <ul className="space-y-1" data-testid="ca-scenarios-list">
              {data.counts_by_scenario.map((r) => (
                <li key={r.scenario} className="flex items-center gap-2 text-[11px]">
                  <div className="w-56 shrink-0 font-mono">{r.scenario}</div>
                  <div className="flex-1 h-3 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-sky-500" style={{ width: `${(r.count / (data.counts_by_scenario[0]?.count || 1)) * 100}%` }} />
                  </div>
                  <div className="w-12 text-right tabular-nums">{r.count}</div>
                </li>
              ))}
              {data.counts_by_scenario.length === 0 && (
                <li className="text-xs italic text-muted-foreground py-3">No scenarios opened yet.</li>
              )}
            </ul>
          </Card>
        </div>

        {/* Per-judge table */}
        <Card className="p-4 rounded-none">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Users size={12} /> Judges · latest {data.judges.length}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="ca-judges-table">
              <thead>
                <tr className="text-left border-b text-muted-foreground">
                  <th className="py-1 pr-2">Judge (hashed IP)</th>
                  <th className="py-1 pr-2 text-right">Events</th>
                  <th className="py-1 pr-2">Scenarios</th>
                  <th className="py-1 pr-2">Objectives</th>
                  <th className="py-1 pr-2 text-right">Resets</th>
                  <th className="py-1 pr-2">Challenge</th>
                  <th className="py-1 pr-2">First seen</th>
                  <th className="py-1 pr-2">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.judges.map((j) => (
                  <tr key={j.ip_hash} className="border-b last:border-0" data-testid={`ca-judge-row-${j.ip_hash}`}>
                    <td className="py-1 pr-2 font-mono">{j.ip_hash}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{j.events}</td>
                    <td className="py-1 pr-2">
                      {j.scenarios.length === 0 ? <span className="text-muted-foreground">—</span> :
                        j.scenarios.map((s) => <Badge key={s} variant="outline" className="mr-1 text-[10px]">{s}</Badge>)}
                    </td>
                    <td className="py-1 pr-2 tabular-nums">{j.objectives_done.length}/3</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{j.reset_count}</td>
                    <td className="py-1 pr-2">
                      {j.challenge_complete ?
                        <span className="text-emerald-600 font-black uppercase text-[10px] tracking-widest">✓ Complete</span> :
                        <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1 pr-2 text-muted-foreground">{j.first_seen ? new Date(j.first_seen).toLocaleString() : '—'}</td>
                    <td className="py-1 pr-2 text-muted-foreground">{j.last_seen ? new Date(j.last_seen).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {data.judges.length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center italic text-muted-foreground">No judge activity yet — send them the demo URL.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-3 rounded-none border-dashed border-2 bg-neutral-50">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs text-neutral-600">
              Live demo entry: <span className="font-mono">{window.location.origin}/competition-demo</span>
            </div>
            <a href="/competition-demo" target="_blank" rel="noreferrer"
              className="text-xs text-sky-600 hover:text-sky-800 uppercase tracking-widest font-black inline-flex items-center gap-1"
              data-testid="ca-open-demo-link">
              Open judge experience <ArrowSquareOut size={12} />
            </a>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

function FunnelStep({ icon: Icon, label, value, tone, testId }) {
  return (
    <Card className={`p-3 rounded-none border-l-4 border-${tone}-500`} data-testid={testId}>
      <Icon size={16} weight="duotone" className={`text-${tone}-500 mb-1`} />
      <div className="text-2xl font-black tabular-nums leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </Card>
  );
}

function JudgeCodesCard() {
  const [codes, setCodes] = useState(null);
  const [counts, setCounts] = useState({ active: 0, used: 0, revoked: 0, expired: 0 });
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [judgeName, setJudgeName] = useState('');
  const [expiresHours, setExpiresHours] = useState('');
  const [notes, setNotes] = useState('');
  const [newCode, setNewCode] = useState(null); // { code, judge_name, expires_at }
  const [statusFilter, setStatusFilter] = useState('all');
  const inviteBase = typeof window !== 'undefined' ? window.location.origin : '';

  const load = async () => {
    setLoading(true);
    try {
      const r = await listJudgeCodes();
      setCodes(r.data.codes);
      setCounts(r.data.counts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load codes failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    if (!judgeName.trim()) { toast.error('Enter a judge name'); return; }
    setCreating(true);
    try {
      const body = { judge_name: judgeName.trim() };
      if (expiresHours) body.expires_hours = Number(expiresHours);
      if (notes.trim()) body.notes = notes.trim();
      const r = await createJudgeCode(body);
      setNewCode(r.data);
      setJudgeName(''); setExpiresHours(''); setNotes('');
      toast.success('Judge code created');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const doRevoke = async (id, name) => {
    if (!window.confirm(`Revoke code for ${name}? They will lose access as soon as they log out (existing session stays live).`)) return;
    try {
      await revokeJudgeCode(id);
      toast.success('Code revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (_) { toast.error('Clipboard blocked — copy manually'); }
  };

  const inviteUrl = (code) => `${inviteBase}/competition-demo/login?code=${encodeURIComponent(code)}`;

  const filtered = (codes || []).filter((c) => statusFilter === 'all' || c.status === statusFilter);

  return (
    <Card className="p-4 rounded-none border-l-4 border-l-sky-500" data-testid="ca-judge-codes-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-sky-600 mb-1">
            <Ticket size={12} weight="duotone" /> Per-Judge Codes
          </div>
          <div className="text-sm font-bold">Single-use access codes tied to a judge name</div>
          <p className="text-xs text-muted-foreground mt-1">
            Each code works exactly once. Revoke individually. Judges enter the code on the demo login screen — no shared password to leak.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="text-emerald-600 font-bold">{counts.active}</span> active ·{' '}
            <span className="font-mono">{counts.used}</span> used ·{' '}
            <span className="text-red-600">{counts.revoked}</span> revoked ·{' '}
            <span className="text-amber-600">{counts.expired}</span> expired
          </div>
          <Button
            onClick={() => setCreateOpen((v) => !v)}
            className="rounded-none bg-sky-600 hover:bg-sky-700 text-white text-xs uppercase tracking-widest"
            data-testid="ca-jc-create-open"
          >
            <Plus size={14} className="mr-1" /> Create code
          </Button>
        </div>
      </div>

      {createOpen && !newCode && (
        <div className="mb-4 border-t pt-3 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Judge name *</span>
            <input value={judgeName} onChange={(e) => setJudgeName(e.target.value)} placeholder="e.g. Alice Nguyen" className="border rounded-none px-2 py-1 text-sm" data-testid="ca-jc-name" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Expires in (hours)</span>
            <input type="number" min={1} max={720} value={expiresHours} onChange={(e) => setExpiresHours(e.target.value)} placeholder="Optional" className="border rounded-none px-2 py-1 text-sm" data-testid="ca-jc-expires" />
          </label>
          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</span>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional — event name, company, etc." className="border rounded-none px-2 py-1 text-sm" data-testid="ca-jc-notes" />
          </label>
          <div className="md:col-span-4 flex gap-2 justify-end pt-1">
            <Button variant="ghost" className="rounded-none text-xs uppercase tracking-widest" onClick={() => { setCreateOpen(false); setJudgeName(''); setExpiresHours(''); setNotes(''); }}>
              Cancel
            </Button>
            <Button
              disabled={creating || !judgeName.trim()}
              onClick={submitCreate}
              className="rounded-none bg-sky-600 hover:bg-sky-700 text-white text-xs uppercase tracking-widest"
              data-testid="ca-jc-create-submit"
            >
              {creating ? 'Creating…' : 'Create code'}
            </Button>
          </div>
        </div>
      )}

      {newCode && (
        <div className="mb-4 border-t pt-3 space-y-2" data-testid="ca-jc-new-code">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-sky-600">
            New code for <span className="text-neutral-900">{newCode.judge_name}</span> · single-use
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="bg-neutral-900 text-sky-300 px-3 py-2 font-mono text-lg tracking-widest select-all" data-testid="ca-jc-new-code-value">
              {newCode.code}
            </code>
            <Button onClick={() => copy(newCode.code, 'Code copied')} className="rounded-none bg-sky-600 hover:bg-sky-700 text-white text-xs uppercase tracking-widest">
              <Copy size={14} className="mr-1" /> Copy code
            </Button>
            <Button onClick={() => copy(inviteUrl(newCode.code), 'Invite link copied')} variant="outline" className="rounded-none text-xs uppercase tracking-widest">
              <Copy size={14} className="mr-1" /> Copy invite link
            </Button>
            <Button onClick={() => { setNewCode(null); setCreateOpen(false); }} variant="ghost" className="rounded-none text-xs uppercase tracking-widest">
              Done
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Invite URL:{' '}
            <a href={inviteUrl(newCode.code)} target="_blank" rel="noreferrer" className="text-sky-700 underline break-all">
              {inviteUrl(newCode.code)}
            </a>
            {newCode.expires_at && (
              <> · Expires {new Date(newCode.expires_at).toLocaleString()}</>
            )}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        Filter:
        {['all', 'active', 'used', 'revoked', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-2 py-0.5 border ${statusFilter === s ? 'bg-sky-600 text-white border-sky-600' : 'border-neutral-300 hover:border-sky-400'}`}
            data-testid={`ca-jc-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="text-xs w-full min-w-[720px]">
          <thead>
            <tr className="text-left text-muted-foreground uppercase tracking-widest text-[10px] border-b">
              <th className="py-2 pr-2">Judge</th>
              <th className="py-2 pr-2">Code</th>
              <th className="py-2 pr-2">Status</th>
              <th className="py-2 pr-2">Created</th>
              <th className="py-2 pr-2">Expires</th>
              <th className="py-2 pr-2">Used</th>
              <th className="py-2 pr-2">Notes</th>
              <th className="py-2 pr-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="ca-jc-table">
            {filtered.map((c) => (
              <tr key={c.id} className="border-b last:border-b-0">
                <td className="py-2 pr-2 font-bold">{c.judge_name || '—'}</td>
                <td className="py-2 pr-2 font-mono">{c.code}</td>
                <td className="py-2 pr-2"><StatusBadge status={c.status} /></td>
                <td className="py-2 pr-2 text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleString() : '—'}</td>
                <td className="py-2 pr-2 text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleString() : 'never'}</td>
                <td className="py-2 pr-2 text-muted-foreground">{c.used_at ? new Date(c.used_at).toLocaleString() : '—'}</td>
                <td className="py-2 pr-2 text-muted-foreground max-w-[200px] truncate" title={c.notes || ''}>{c.notes || '—'}</td>
                <td className="py-2 pr-2 text-right">
                  <div className="inline-flex gap-1">
                    {c.status === 'active' && (
                      <>
                        <button onClick={() => copy(inviteUrl(c.code), 'Invite link copied')}
                          className="text-sky-600 hover:text-sky-800 text-[11px] uppercase tracking-widest px-1"
                          title="Copy invite link" data-testid={`ca-jc-copy-${c.id}`}>
                          <Copy size={12} />
                        </button>
                        <button onClick={() => doRevoke(c.id, c.judge_name)}
                          className="text-red-600 hover:text-red-800 text-[11px] uppercase tracking-widest px-1"
                          title="Revoke" data-testid={`ca-jc-revoke-${c.id}`}>
                          <Prohibit size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(!codes || filtered.length === 0) && (
              <tr><td colSpan={8} className="py-6 text-center italic text-muted-foreground">
                {loading ? 'Loading…' : (codes && codes.length === 0
                  ? 'No codes yet — create one to invite a specific judge.'
                  : 'No codes match this filter.')}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    used: 'bg-slate-200 text-slate-700 border-slate-300',
    revoked: 'bg-red-100 text-red-700 border-red-300',
    expired: 'bg-amber-100 text-amber-800 border-amber-300',
  };
  const cls = map[status] || 'bg-neutral-100 text-neutral-700 border-neutral-300';
  return <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest border ${cls}`}>{status}</span>;
}
